import { Descendant } from 'slate'

import { isValidUrl, TypographyGroupValue } from '@makyoapp/makyo-common'
import { CornerRadius, HoverEffectVariant, RawSpacingInput } from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentInfos } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder'
import { getSafely } from '@/shared/safely'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { CompAudioProps } from './comp'

export interface CompAudioWrapperCoreProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
  children: (props: CompAudioProps) => React.ReactNode
}

export function CompAudioWrapperCore({
  infos: { component, settings, containerClient, detailTableId, detailRowIndex, currentParentBg },
  action,
  children,
}: CompAudioWrapperCoreProps) {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  // ========================= data =========================
  const src = useParseVariableSelect<string>(
    settings?.[ComponentSettingKey.AudioDataSrc],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const cover = useParseVariableSelect<string>(
    settings?.[ComponentSettingKey.AudioDataCover],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const header = useParseVariableText(
    (settings?.[ComponentSettingKey.AudioDataHeader] as Descendant[]) ?? [],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const subheader = useParseVariableText(
    (settings?.[ComponentSettingKey.AudioDataSubHeader] as Descendant[]) ?? [],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const text = useParseVariableText(
    (settings?.[ComponentSettingKey.AudioDataText] as Descendant[]) ?? [],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  // ========================= design =========================

  // base
  const width = getSafely(settings, [ComponentSettingKey.AudioDesignWidth], ['100%'])[0]
  const alignment = settings[ComponentSettingKey.AudioDesignHAlign] ?? 'parent'
  const margin = settings?.[ComponentSettingKey.AudioDesignMargin] as RawSpacingInput
  const padding = settings?.[ComponentSettingKey.AudioDesignPadding] as RawSpacingInput

  // audio
  const colorVariant = settings?.[ComponentSettingKey.AudioDesignColorVariant] ?? 'primary-20'
  const cornerRadius = settings?.[ComponentSettingKey.AudioDesignShape] as CornerRadius

  // typography
  const typoSetting = settings?.[ComponentSettingKey.AudioDesignTypography] as TypographyGroupValue
  const subheaderTypo = typoSetting?.[ComponentSettingKey.AudioDesignSubheaderTypography]
  const headerTypo = typoSetting?.[ComponentSettingKey.AudioDesignHeaderTypography]
  const supportingTextTypo = typoSetting?.[ComponentSettingKey.AudioDesignSupportingTextTypography]
  const timerTypo = typoSetting?.[ComponentSettingKey.AudioDesignTimerTypography]

  const hoverEffect = settings?.[ComponentSettingKey.AudioDesignHoverEffect] as HoverEffectVariant

  // ========================= derived =========================

  const shouldShow = !!src && isValidUrl(src)
  if (!shouldShow) return null

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  const audioProps: CompAudioProps = {
    // base
    id: component._id,
    width,
    alignment,
    margin,
    backgroundColor: colorVariant ?? currentParentBg,

    // audio
    source: src,
    coverImage: cover,
    title: header,
    subtitle: subheader,
    supportingText: text,
    cornerRadius,
    titleTypography: headerTypo,
    subtitleTypography: subheaderTypo,
    supportingTextTypography: supportingTextTypo,
    timerTypography: timerTypo,
    hoverEffect,
    hoverIntensity: 'md',
  }

  if (isDevelopmentMode) {
    return (
      <div className='relative flex flex-col w-full border border-dashed' style={{ borderColor: '#333' }}>
        {children(audioProps)}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return children(audioProps)
}
